import React, { useContext, useEffect, useState } from "react";
import useAssetTableJson from "../../../../../json/useAssetTableJson";
import { AppContext } from "../../../../../context/context";
import useUrlParams from "../../../../../customHooks/useUrlParams";

import {
  AtsSubsidiaryEdge,
  AtsSubsidiaryOrderField,
  GetSubsidiariesResult,
  GetSubsidiariesVars,
} from "../../masters/subsidary/queries/types";
import {
  GridAlignment,
  GridColDef,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import { FETCH_MORE_DATA, ROWS_PER_PAGE } from "../../../../../utils/constants";
import useLabels from "../../../../../customHooks/useLabels";
import { TableHeaderProps } from "../../../../../utils/types";
import { TABLE_ROW_HEIGHT } from "../../../../../styles/Variables";
import { useLazyQuery } from "@apollo/client";
import { GetSubsidiaries } from "../../masters/subsidary/queries/queries";
import { getModifiedScrollHeight } from "../../../../../utils/UtilFunctions";
import { OrderDirection } from "../../../../../utils/commonQueryTypes";
import { Title } from "../../../../../stories/Title/Title";
import { StyledDatagrid } from "../../../../../styles/DatagridStyles";
import NoData from "../../../../../images/NoData.svg";
import { Checkbox } from "@mui/material";

interface Props {
  selectedIds: number[];
  handleCheckboxChange: (id: number) => void;
}

const SubsidiariesList = ({ selectedIds, handleCheckboxChange }: Props) => {
  const { UserRightsConfig } = useAssetTableJson();
  const { state } = useContext(AppContext);
  const { customer_id } = useUrlParams();
  

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [subsidiries, setSubsidiries] = useState<AtsSubsidiaryEdge[]>([]);
  const [rows, setRows] = useState<GridValidRowModel[]>([]);
  const [searchData, setSearchData] = useState("");
  const { subsidiaryLabel } = useLabels();
  const dynamicHeaders: TableHeaderProps[] = UserRightsConfig.List.map(
    (header) => ({
      headerName: header.headerName,
      cellClassName: header.cellClassName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      hideable: header.hideable,
      renderCell: (params) => {
        if (header.field === "checkbox") {
          return (
            <>
              <Checkbox
                value={params.row}
                checked={selectedIds.includes(params.row.subId)}
                onChange={() => handleCheckboxChange(params.row.subId)}
                inputProps={{ "aria-label": "controlled" }}
              />
            </>
          );
        }

        return params.value;
      },
    })
  );
  const columns: GridColDef[] = [...dynamicHeaders];

  const [GetSubsidiary, { data, loading, fetchMore }] = useLazyQuery<
    GetSubsidiariesResult,
    GetSubsidiariesVars
  >(GetSubsidiaries);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetSubsidiaries.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = subsidiries.find(
            (sub) => sub.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setSubsidiries(updatedNewData);
        setRows(
          updatedNewData.map(({ node }, index) => ({
            id: index + 1,
            name: node.name,
            company_name: node.name,
            company_addr: node.address,
            company_email: node.email,
            company_mobile: node.mobile,
            company_c_person: node.contactPerson,
            subId: node.id,
          }))
        );
      } else {
        setSubsidiries(newData);
        setRows(
          newData.map(({ node }, index) => ({
            id: index + 1,
            name: node.name,
            company_name: node.name,
            company_addr: node.address,
            company_email: node.email,
            company_mobile: node.mobile,
            company_c_person: node.contactPerson,
            subId: node.id,
          }))
        );
      }
      setEndCursor(data.GetSubsidiaries.pageInfo.endCursor);
    } // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    const scrollTable = document.getElementsByClassName(
      "MuiDataGrid-virtualScroller"
    )[0] as Element;
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
        if (hasNextPage && !loading) {
          fetchMore({
            variables: {
              first: FETCH_MORE_DATA,
              after: endCursor,
            },
            updateQuery: (prevResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prevResult;

              const newEdges = fetchMoreResult.GetSubsidiaries.edges;
              const pageInfo = fetchMoreResult.GetSubsidiaries.pageInfo;
              setEndCursor(pageInfo.endCursor);
              setHasNextPage(pageInfo.hasNextPage);

              const duplicateCheck = prevResult.GetSubsidiaries.edges.filter(
                ({ node: { id } }) =>
                  newEdges.findIndex(
                    ({ node: { id: newId } }) => newId === id
                  ) !== -1
              );

              if (duplicateCheck.length > 0) return prevResult;

              return {
                GetSubsidiaries: {
                  edges: [...subsidiries, ...newEdges],
                  pageInfo,
                  totalCount: data ? data.GetSubsidiaries.totalCount! : 0,
                },
              };
            },
          });
        }
      }
    };
    if (scrollTable && rows.length)
      scrollTable.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollTable) scrollTable.removeEventListener("scroll", handleScroll);
    };
  }, [rows]);

  useEffect(() => {
    if (customer_id && state.companyId) {
      GetSubsidiary({
        variables: {
          after: null,
          customerID: customer_id,
          companyID: state.companyId ? state.companyId : 0,
          empID: 0,
          first: ROWS_PER_PAGE,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsSubsidiaryOrderField.NAME,
          },
          
          name: searchData,
        },
      });
    }
  }, [customer_id, state.companyId, GetSubsidiary, searchData]);

  return (
    <>
      <Title>{subsidiaryLabel}</Title>
      <div className="subsidary__tableblock">
        {rows.length ? (
          <StyledDatagrid
            columns={columns}
            rows={rows}
            rowHeight={TABLE_ROW_HEIGHT}
            disableRowSelectionOnClick
            hideFooter
          />
        ) : (
          <>
            <img src={NoData} alt="" className="nodata-image" />
          </>
        )}
      </div>
    </>
  );
};

export default SubsidiariesList;
